import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { getApiErrorMessage, getApiErrorStatus } from '../../utils/apiError';
import toast from 'react-hot-toast';
import styles from './RegisterForm.module.css';

function validateEmail(email: string): string | null {
  if (!email) return 'Введите email';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Некорректный формат email';
  return null;
}

function validatePassword(password: string): string | null {
  if (!password) return 'Введите пароль';
  if (password.length < 8) return 'Пароль должен содержать минимум 8 символов';
  // bcrypt на сервере принимает не больше 72 байт
  if (new TextEncoder().encode(password).length > 72) {
    return 'Пароль слишком длинный: максимум 72 символа латиницей (русская буква считается за два)';
  }
  return null;
}

export function RegisterForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const referralCode = searchParams.get('ref') || undefined;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState('');
  const [emailTaken, setEmailTaken] = useState(false);
  const [loading, setLoading] = useState(false);

  function validate(trimmedEmail: string): boolean {
    const newErrors: Record<string, string> = {};

    const emailErr = validateEmail(trimmedEmail);
    if (emailErr) newErrors.email = emailErr;

    const passErr = validatePassword(password);
    if (passErr) newErrors.password = passErr;

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Подтвердите пароль';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Пароли не совпадают';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setServerError('');
    setEmailTaken(false);

    const trimmedEmail = email.trim();
    if (!validate(trimmedEmail)) return;

    setLoading(true);
    try {
      await register(trimmedEmail, password, referralCode);
      toast.success('Код подтверждения отправлен на ваш email');
      navigate('/verify-email');
    } catch (err: unknown) {
      setServerError(getApiErrorMessage(err, 'Ошибка при регистрации. Попробуйте позже.'));
      setEmailTaken(getApiErrorStatus(err) === 409);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.card}>
        <h1 className={styles.title}>Создать аккаунт</h1>
        <p className={styles.subtitle}>Присоединяйтесь к Norion</p>

        {emailTaken ? (
          <div className={styles.serverError}>
            Аккаунт с такой почтой уже зарегистрирован. <Link to="/login">Войдите</Link> или,
            если забыли пароль, воспользуйтесь{' '}
            {/* почту передаём дальше, чтобы не вводить её второй раз */}
            <Link to="/forgot-password" state={{ email: email.trim() }}>
              формой сброса пароля
            </Link>
            .
          </div>
        ) : (
          serverError && <div className={styles.serverError}>{serverError}</div>
        )}

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <Input
            label="Email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <Input
            label="Пароль"
            type="password"
            placeholder="Минимум 8 символов"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            autoComplete="new-password"
          />
          <Input
            label="Подтверждение пароля"
            type="password"
            placeholder="Повторите пароль"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={errors.confirmPassword}
            autoComplete="new-password"
          />
          <Button type="submit" fullWidth loading={loading} size="lg">
            Создать аккаунт
          </Button>
        </form>

        <p className={styles.footer}>
          Уже есть аккаунт?{' '}
          <Link to="/login">Войти</Link>
        </p>
      </div>
    </div>
  );
}
